import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons for Leave Master
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ReportIcon from '@mui/icons-material/Report';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import EventNoteIcon from '@mui/icons-material/EventNote';

// Import split components
import LeaveAllocationAssign from './LeaveAllocationAssign';
import LeaveAllocationHistory from './LeaveAllocationHistory';
import LeaveUnallocationAssign from './LeaveUnallocationAssign';
import LeaveUnallocationHistory from './LeaveUnallocationHistory';
import LeaveNHAdd from './LeaveNHAdd';
import LeaveNHManage from './LeaveNHManage';

import api from '../../api';

const LeaveMaster = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const empId = localStorage.getItem('emp_id');
  const [activeTab, setActiveTab] = useState('alloc_assign');
  const [departments, setDepartments] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg: Array.isArray(msg) ? msg[0]?.msg : typeof msg === 'object' ? JSON.stringify(msg) : msg, type });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      const deptRes = await api.get('/settings/departments');
      setDepartments(deptRes.data);
    } catch (err) { console.error(err); }
  };

  // UPDATED: NavItem with icon and theme-based styling
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeTab === id} 
        onClick={() => setActiveTab(id)} 
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
          '&.Mui-selected .MuiListItemText-primary': { 
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeTab === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeTab === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  // Section Header with theme-based styling
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop,
      gap: 0
    }}>
      
      {/* LEFT NAVIGATION PANEL */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <SectionHeader title="LEAVE ALLOCATIONS" />
          <NavItem id="alloc_assign" label="Assign Leave" icon={<AssignmentIcon fontSize="small" />} />
          <NavItem id="alloc_history" label="Assigned Leave (Report)" icon={<HistoryIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />
          
          <SectionHeader title="LEAVE UNALLOCATION" />
          <NavItem id="unalloc_assign" label="Unallocate Balance" icon={<RemoveCircleIcon fontSize="small" />} />
          <NavItem id="unalloc_history" label="Unallocation Report" icon={<ReportIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="LEAVE DETAILS" />
          <NavItem id="nh_add" label="Add National Holiday" icon={<HolidayVillageIcon fontSize="small" />} />
          <NavItem id="nh_manage" label="Manage NH Dates" icon={<EventNoteIcon fontSize="small" />} />
          
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        bgcolor: '#fbfbfb',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
      }}>
        {/* Header with title */}
        <Box sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0', 
          bgcolor: '#fbfbfb',
          flexShrink: 0,
        }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.9rem' }}>
            {activeTab.replace('_', ' ')}
          </Typography>
        </Box>
        
        {/* Content area with independent scrolling */}
        <Box sx={{ 
          flexGrow: 1, 
          width: '100%', 
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 2,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}>
          {activeTab === 'alloc_assign' && <LeaveAllocationAssign empId={empId} departments={departments} showMsg={showMsg} setActiveTab={setActiveTab} />}
          {activeTab === 'alloc_history' && <LeaveAllocationHistory empId={empId} showMsg={showMsg} />}
          {activeTab === 'unalloc_assign' && <LeaveUnallocationAssign empId={empId} departments={departments} showMsg={showMsg} setActiveTab={setActiveTab} />}
          {activeTab === 'unalloc_history' && <LeaveUnallocationHistory empId={empId} showMsg={showMsg} />}
          {activeTab === 'nh_add' && <LeaveNHAdd empId={empId} showMsg={showMsg} setActiveTab={setActiveTab} />}
          {activeTab === 'nh_manage' && <LeaveNHManage empId={empId} showMsg={showMsg} />}
        </Box>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default LeaveMaster;